import React, { useEffect, useState } from 'react';
import { useUser } from '../../context';
import storyService from '../../services/storyService';
import { setPref, readPref } from '../../services/prefsService';
import usePageScrollLock from '../../utils/usePageScrollLock';
import useMediaQuery from '../../utils/useMediaQuery';
import StoryViewer from '../stories/StoryViewer';
import PortalEditSheet from './PortalEditSheet';
import { Icon } from '../onboarding/onboardingUi';
import { safePad } from '../../utils/safeArea';

/**
 * After GO LIVE with a photo (Oct 30 2026 handoff): "Make an introduction." over the landing page,
 * then EDIT PORTAL PAGE, then your own introduction in the viewer and a YOU ring on the rail.
 * INTRODUCE YOURSELF in the editor is the ONLY thing that creates one, so a person who closes this
 * has no portal up. The veil, × and the second button all dismiss it for good, on the account.
 *
 * `onDone` fires once this has nothing left to show, including straight away for someone with no
 * photo or who has already used theirs.
 */
export default function IntroductionFlow({ city, onDone }) {
  const { profile } = useUser();
  const [step, setStep] = useState('checking');   // checking | sheet | editor | viewer
  const [reels, setReels] = useState([]);

  useEffect(() => {
    let on = true;
    if (readPref('introSheet.dismissed')) { onDone?.(); return undefined; }
    storyService.getMyIntroduction().then((r) => {
      if (!on) return;
      if (r?.success && r.data?.canIntroduce) setStep('sheet'); else onDone?.();
    });
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => { setPref('introSheet.dismissed', true); onDone?.(); };

  const introduced = async () => {
    setPref('introSheet.dismissed', true);
    const r = await storyService.getReel(profile?.userId);
    if (r?.success && r.data) { setReels([r.data]); setStep('viewer'); } else onDone?.();
  };

  if (step === 'sheet') return <MakeIntroductionSheet city={city} onIntroduce={() => setStep('editor')} onDismiss={dismiss} />;
  if (step === 'editor') return <PortalEditSheet onClose={dismiss} onIntroduced={introduced} />;
  if (step === 'viewer') return <StoryViewer reels={reels} startUserId={profile?.userId} onClose={() => onDone?.()} />;
  return null;
}

/** The bottom sheet (a 480px centred modal on desktop) over a 62% veil. */
export function MakeIntroductionSheet({ city, onIntroduce, onDismiss }) {
  const desk = useMediaQuery('(min-width: 900px)');
  usePageScrollLock(true);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onDismiss?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);
  return (
    <div className="pk fixed inset-0 z-[90] flex justify-center" data-testid="intro-sheet-veil" onClick={onDismiss} style={{ alignItems: desk ? 'center' : 'flex-end', background: 'rgba(0,0,0,.62)', color: '#fff' }}>
      <div role="dialog" aria-modal="true" aria-label="Make an introduction" data-testid="intro-sheet" onClick={(e) => e.stopPropagation()} style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 14, width: desk ? 480 : '100%', padding: safePad('26px 22px 22px'), boxSizing: 'border-box', borderRadius: desk ? 32 : '28px 28px 0 0', background: '#000', boxShadow: '0 0 0 1px #262626' }}>
        <button type="button" onClick={onDismiss} aria-label="Close" data-testid="intro-sheet-close" className="pk-btn pk-secondary-on-black" style={{ position: 'absolute', top: 18, right: 18, width: 40, height: 40, padding: 0, borderRadius: '50%', background: '#1F1F1F' }}>{Icon.close}</button>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.18em', color: '#22ECFB' }}>YOU’RE IN · {(city || '').toUpperCase()}</span>
        <span style={{ paddingRight: 48, fontFamily: "'Times New Roman', Times, serif", fontStyle: 'italic', fontSize: 34, lineHeight: 1.1 }}>Make an introduction.</span>
        <span style={{ fontSize: 13, lineHeight: 1.65, color: '#D4D4D4' }}>Your profile sits in the Portal Display for 7 days. Your people will find you there. A finished page gets more adds than an empty one.</span>
        <button type="button" onClick={onIntroduce} data-testid="intro-sheet-go" className="pk-btn pk-primary-on-black" style={{ height: 56, marginTop: 6, background: '#fff', color: '#000', fontSize: 12, fontWeight: 800, letterSpacing: '.16em' }}>INTRODUCE YOURSELF →</button>
        <button type="button" onClick={onDismiss} data-testid="intro-sheet-skip" className="pk-btn" style={{ height: 52, background: '#1F1F1F', color: '#fff', fontSize: 11, fontWeight: 800, letterSpacing: '.16em' }}>I’LL FIND FRIENDS ON MY OWN</button>
      </div>
    </div>
  );
}
